import React, { useState } from 'react';

import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import { ArrowRight, SyncAlt } from '@material-ui/icons';
import classnames from 'classnames';
import { useClickAway } from 'ui-utils';

import { Typography } from 'components/v2';
import { useNextyContext } from 'containers/App/hooks';
import usePermissions from 'hooks/usePermissions';
import useToggle from 'hooks/useToggle';
import NestedMenuPopover from 'screens/MissionControl/components/NestedMenuPopover';
import useAccessControlItems from 'screens/MissionControl/hooks/useAccessControlItems';

import { useStyles } from './SettingsDrawerMenu.useStyles';

interface SettingsDrawerMenuProps extends WithStyles<typeof useStyles> {}

const SettingsDrawerMenu: React.FC<SettingsDrawerMenuProps> = ({ classes }) => {
  const { hasAccessToProduct } = usePermissions();
  const [open, toggle] = useToggle();
  const { nextyStorageValue } = useNextyContext();
  const [anchorElements, setAnchorElements] = useState<HTMLElement[]>([]);
  const isSelected = (key: string) => window.location.pathname.includes(`/v2/settings/${key}`);
  const clickAwayRef = useClickAway(() => setAnchorElements([]));

  const accessControlItems = useAccessControlItems();

  return (
    <div className={classnames('no-print', classes.root)} onMouseLeave={() => setAnchorElements([])} ref={clickAwayRef}>
      <NestedMenuPopover
        anchorElements={anchorElements}
        setAnchorElements={setAnchorElements}
        nestedSettingsTabs={accessControlItems}
      />
      <Drawer
        variant="permanent"
        className={classnames({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
          [classes.drawerNexty]: nextyStorageValue,
          [classes.drawer]: !nextyStorageValue,
        })}
        classes={{
          paper: classnames({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
            [classes.drawerNexty]: nextyStorageValue,
            [classes.drawer]: !nextyStorageValue,
          }),
        }}
        open={open}
      >
        <div
          className={classnames(classes.toolbar, {
            [classes.toolbarClosed]: !open,
          })}
        >
          {open && <Typography variant="h3W600F15">Mission Control</Typography>}
          <IconButton
            color="primary"
            className={classes.menuToggle}
            onClick={() => {
              toggle();
              setAnchorElements([]);
            }}
          >
            <SyncAlt />
          </IconButton>
        </div>
        <List classes={{ root: classes.listRoot }}>
          {accessControlItems.map(({ action, label, key, icon: Icon, subMenu, provisioning }) => {
            // hide not provisioned items
            if (provisioning.length && !hasAccessToProduct(provisioning)) {
              return null;
            }

            if (key === 'spark-management' && subMenu && subMenu.length === 0) {
              return null;
            }

            return (
              <ListItem
                className={classnames(classes.listItem, {
                  [classes.listItemSelected]: isSelected(key),
                })}
                button
                id={key}
                data-lh-id={`mission-control-side-menu-${key}-item`}
                key={key}
                onClick={action}
                onMouseEnter={(event) => {
                  // NOTE: [line below] if event.currentTarget isn't "cached" (set to a const) it will be nullified
                  // by react when it hyjacks and pools native brower events (which are wrapped with SyntheticEvents).
                  // refer to: https://duncanleung.com/fixing-react-warning-synthetic-events-in-setstate/
                  const currentTarget = event.currentTarget;
                  setAnchorElements(() => [currentTarget]);
                }}
                selected={isSelected(key)}
              >
                <ListItemIcon>
                  <span>
                    {Icon && <Icon className={classes.icon} fontSize="small" color="primary" />}
                    {!open && subMenu && <ArrowRight className={classes.arrow} color="primary" />}
                  </span>
                </ListItemIcon>
                <ListItemText
                  classes={{
                    root: classes.listItemTextRoot,
                  }}
                  primary={
                    <Typography
                      variant="h3W500F15"
                      className={classnames(classes.listItemText, { [classes.hide]: !open })}
                    >
                      {label}
                      {subMenu && <ArrowRight className={classes.arrow} color="primary" />}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </div>
  );
};

export default withStyles(useStyles)(SettingsDrawerMenu);
